/**
 * ORKESTRA ŞEFİ OTOMASYON AYARI (Faz 4) — Ayarlar > Orkestra Şefi sekmesi.
 * Kullanıcı kararı: varsayılan Danışman; ama buradan yükseltilebilir olmalı.
 * Seviye 1-2 Pro/Ajans ayrıcalığı — kısıtlıysa neden açıkça yazılır.
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ShieldCheck, Wand2, Rocket, Lock } from "lucide-react";

type Ayar = { seviye: 0 | 1 | 2; haftalikTavan: number | null; yukseltilebilir: boolean; kisitNedeni: string | null };

const SEVIYELER = [
  {
    no: 0 as const, ad: "Danışman", ikon: ShieldCheck,
    aciklama: "Asistan yalnız önerir; her iş senin tek tık onayınla takvime iner. En güvenli mod — herkes için varsayılan.",
  },
  {
    no: 1 as const, ad: "Yarı otomatik", ikon: Wand2,
    aciklama: "Görev tipi işleri (sayfa güçlendirme görevi, inceleme, brief hazırlığı) asistan kendisi takvime koyar — bunlar token yakmaz. Token yakan ajan koşuları yine onayını bekler.",
  },
  {
    no: 2 as const, ad: "Otopilot", ikon: Rocket,
    aciklama: "Asistan, senin belirlediğin haftalık token tavanı içinde ajan koşularını da kendisi planlar. Tavanı aşan işler öneri olarak bekler; üretilen içerik hiçbir seviyede onaysız yayınlanmaz.",
  },
];

export function AsistanAyarlarPanel() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const { toast } = useToast();
  const qc = useQueryClient();

  const ayar = useQuery<Ayar>({
    queryKey: ["asistan-ayarlar", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/asistan-ayarlar`)).json(),
  });

  const [seviye, setSeviye] = useState<0 | 1 | 2>(0);
  const [tavan, setTavan] = useState<string>("1000");
  const [kaydediliyor, setKaydediliyor] = useState(false);
  useEffect(() => {
    if (ayar.data) {
      setSeviye(ayar.data.seviye);
      if (ayar.data.haftalikTavan) setTavan(String(ayar.data.haftalikTavan));
    }
  }, [ayar.data]);

  const kaydet = async () => {
    if (!activeDomainId || kaydediliyor) return;
    setKaydediliyor(true);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/asistan-ayarlar`, {
        method: "PATCH", headers: { "content-type": "application/json" },
        body: JSON.stringify({ seviye, ...(seviye === 2 ? { haftalikTavan: Number(tavan) } : {}) }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      if (!r.ok) throw new Error(j.error ?? "Kaydedilemedi");
      toast({ title: "Asistan ayarı kaydedildi", description: `${SEVIYELER[seviye].ad} modu aktif — bir sonraki haftalık plandan itibaren geçerli.` });
      void qc.invalidateQueries({ queryKey: ["asistan-ayarlar", activeDomainId] });
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setKaydediliyor(false); }
  };

  if (ayar.isLoading) return <Card className="p-8 text-center text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />Ayar okunuyor…</Card>;
  const kisitli = ayar.data && !ayar.data.yukseltilebilir;

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="text-[13px] font-semibold mb-1">✨ Geolop Asistanı ne kadar yetkili olsun?</div>
        <p className="text-[12px] text-muted-foreground max-w-2xl">
          Geolop Asistanı her pazartesi Fırsat Havuzu'ndan haftanın 5-8 işini seçer. Bu ayar, seçtiklerini takvime kendisinin koyup
          koyamayacağını belirler — aktif site ({domains?.find((d) => d.id === activeDomainId)?.url?.replace(/^https?:\/\//, "") ?? ""}) için geçerlidir.
        </p>
        {kisitli && (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-[12px]">
            <Lock className="h-3.5 w-3.5 shrink-0 text-amber-600" /> {ayar.data!.kisitNedeni}
          </div>
        )}
      </Card>

      <div className="grid gap-3 md:grid-cols-3">
        {SEVIYELER.map((s) => {
          const Ikon = s.ikon;
          const secili = seviye === s.no;
          const kilitli = s.no >= 1 && kisitli;
          return (
            <button key={s.no} disabled={kilitli} onClick={() => setSeviye(s.no)}
              className={`rounded-xl border p-4 text-left transition disabled:cursor-not-allowed disabled:opacity-45 ${secili ? "border-brand-500 ring-2 ring-brand-500/25" : "hover:border-gray-300 dark:hover:border-white/20"}`}>
              <div className="flex items-center gap-2">
                <Ikon className="h-4 w-4" style={{ color: secili ? "#e83e3c" : undefined }} />
                <span className="text-[13.5px] font-bold">{s.ad}</span>
                {s.no === 0 && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[9.5px] font-semibold text-gray-500 dark:bg-white/[0.08] dark:text-gray-400">VARSAYILAN</span>}
                {kilitli && <Lock className="ml-auto h-3.5 w-3.5 text-muted-foreground" />}
              </div>
              <p className="mt-1.5 text-[11.5px] leading-relaxed text-muted-foreground">{s.aciklama}</p>
            </button>
          );
        })}
      </div>

      {seviye === 2 && (
        <Card className="p-4">
          <div className="text-[12.5px] font-semibold">Haftalık token tavanı</div>
          <p className="mb-2 text-[11.5px] text-muted-foreground">Otopilot bir haftada en fazla bu kadar token'lık ajan koşusu planlayabilir (100 - 50.000). Aylık paket hakkın ayrıca her koşuda denetlenir.</p>
          <div className="flex items-center gap-2">
            <input type="number" min={100} max={50000} step={100} value={tavan} onChange={(e) => setTavan(e.target.value)}
              className="w-36 rounded-lg border bg-transparent px-3 py-2 text-[13px] outline-none dark:border-white/15" />
            <span className="text-[12px] text-muted-foreground">token / hafta (örnek: bir AI görünürlük koşusu ~750, teknik denetim ~150)</span>
          </div>
        </Card>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={() => void kaydet()} disabled={kaydediliyor} className="gap-2 bg-brand-500 hover:bg-brand-600">
          {kaydediliyor ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Kaydet
        </Button>
        <span className="text-[11.5px] text-muted-foreground">İki kural her seviyede değişmez: içerik onaysız yayınlanmaz, aylık paket bütçesi aşılmaz.</span>
      </div>
    </div>
  );
}
